import { Button, makeStyles, Theme } from '@material-ui/core';
import DeleteIcon from '@material-ui/icons/Delete';
import DoneIcon from '@material-ui/icons/Done';
import EditIcon from '@material-ui/icons/Edit';
import UndoIcon from '@material-ui/icons/Undo';
import { GridCellProps } from '@progress/kendo-react-grid';
import React from 'react';
import DeleteButtonWithIcon from '../../ui/DeleteButtonWithConfirmation';
import DiscardButtonWithConfirmation from '../../ui/DiscardButtonWithConfirmation';

const useStyles = makeStyles((theme: Theme) => ({
    root: {
        display: 'flex',
        justifyContent: 'space-evenly',
        alignItems: 'center',
    },
    actionButton: {
        minWidth: 24,
        maxWidth: 30,
        padding: '4px 5px',
    },
    actionButtonIcon: {
        fontSize: 16,
    },
}));

interface ActionsCellProps extends GridCellProps {
    idField: string;
    editField: string;
    isLoading: boolean;
    add: (dataItem: any) => void;
    update: (dataItem: any) => void;
    remove: (dataItem: any) => void;
    edit: (dataItem: any) => void;
    discard: (dataItem: any) => void;
    cancel: (dataItem: any) => void;
    validate?: (dataItem: any) => boolean;
    showDelete?: boolean;
    confirmationMessage: string;
    securityDiscardConfirm?: boolean;
    discardMessage?: string;
    gridData?: any;
    withoutHeaderStyle?: any;
    showEdit?: boolean;
    showUpdate?: boolean;
}

const ActionsCell: React.FC<ActionsCellProps> = ({
    className,
    dataItem,
    validate,
    showDelete,
    confirmationMessage,
    securityDiscardConfirm,
    discardMessage,
    gridData,
    showEdit = true,
    showUpdate = true,
    children,
    ...props
}) => {
    const classes = useStyles();
    const inEdit = dataItem[props.editField];
    const isNewItem = dataItem[props.idField] === -1;

    return inEdit ? (
        <td className={className}>
            <div className={classes.root}>
                {showUpdate && (
                    <Button
                        disabled={props.isLoading}
                        variant="contained"
                        color="primary"
                        size="small"
                        className={classes.actionButton}
                        onClick={() => {
                            const execCondition = !validate || validate!(dataItem);
                            if (execCondition) {
                                isNewItem ? props.add(dataItem) : props.update(dataItem);
                            }
                        }}
                    >
                        <DoneIcon className={classes.actionButtonIcon} />
                    </Button>
                )}
                &nbsp;
                {securityDiscardConfirm && dataItem.isModified ? (
                    <DiscardButtonWithConfirmation
                        dataItem={dataItem}
                        message={discardMessage}
                        color="secondary"
                        className={classes.actionButton}
                        size="small"
                        isLoading={props.isLoading}
                        onDiscardConfirmed={isNewItem ? props.discard : props.cancel}
                        variant="contained"
                    >
                        <UndoIcon className={classes.actionButtonIcon} />
                    </DiscardButtonWithConfirmation>
                ) : (
                    <Button
                        variant="contained"
                        color="secondary"
                        size="small"
                        className={classes.actionButton}
                        onClick={() => (isNewItem ? props.discard(dataItem) : props.cancel(dataItem))}
                    >
                        <UndoIcon className={classes.actionButtonIcon} />
                    </Button>
                )}
                &nbsp;
                {children}
            </div>
        </td>
    ) : (
        <td className={className}>
            <div className={classes.root}>
                {showEdit && (
                    <Button
                        variant="contained"
                        color="primary"
                        size="small"
                        className={classes.actionButton}
                        onClick={() => props.edit(dataItem)}
                    >
                        <EditIcon className={classes.actionButtonIcon} />
                    </Button>
                )}
                &nbsp;
                {(showDelete == undefined || showDelete) && (
                    <DeleteButtonWithIcon
                        dataItem={dataItem}
                        message={confirmationMessage}
                        color="secondary"
                        className={classes.actionButton}
                        size="small"
                        isLoading={props.isLoading}
                        onDeleteConfirmed={props.remove}
                        gridData={gridData}
                        withoutHeaderStyle={props?.withoutHeaderStyle}
                    >
                        <DeleteIcon className={classes.actionButtonIcon} />
                    </DeleteButtonWithIcon>
                )}
                &nbsp;
                {children}
            </div>
        </td>
    );
};

export default ActionsCell;
